/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  uiTypes,
  uiSelectors,
  layerActions,
  layerSelectors,
} from '@opengeoweb/store';
import { LayerInfoButton } from '@opengeoweb/webmap-react';

interface LayerInfoButtonProps {
  isLayerMissing?: boolean;
  isFeatureLayer?: boolean;
  mapId: string;
  serviceUrl: string;
  layerName: string;
  layerId: string;
  source?: uiTypes.Source;
}

export const LayerInfoButtonConnect: React.FC<LayerInfoButtonProps> = ({
  isLayerMissing,
  isFeatureLayer,
  mapId,
  serviceUrl,
  layerName,
  layerId,
  source = 'app',
}: LayerInfoButtonProps) => {
  const dispatch = useDispatch();

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.LayerInfo),
  );

  const currentLayerInfo = useSelector((store: CoreAppStore) =>
    layerSelectors.getActiveLayerInfo(store),
  );

  const currentLayerStyle = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerStyle(store, layerId),
  );

  const isActive =
    layerName === currentLayerInfo?.layerName &&
    serviceUrl === currentLayerInfo?.serviceUrl &&
    isOpenInStore;

  const onClick = (): void => {
    if (!isActive) {
      dispatch(
        layerActions.showLayerInfo({
          layerName,
          serviceUrl,
          source,
          mapId,
          style: currentLayerStyle,
        }),
      );
    } else {
      dispatch(layerActions.hideLayerInfo());
    }
  };

  return (
    <LayerInfoButton
      disabled={isLayerMissing || isFeatureLayer}
      isActive={isActive}
      onClick={onClick}
    />
  );
};
